import { BCMSDate } from "bcms-react";
import { Deposit } from "model";
/**
 * @class DepositHistorySearch
 * @classdesc Class definition for controlling the properties that are set in the search workflow
 * 
 * @property {STRING} _date - The date offset to filter by
 * @property {STRING} _description - The description to filter by
 * @property {STRING} _type - The type of transactions to filter by
 * @property {STRING} _amount - The amount to filter by
 * @property {STRING} _check - The check number to filter by
 * @property {Map} _ranges - The values and labels set in the custom select
 * @property {Map} _types - The values and labels set in the custom select
 * @property {Map} _account - The account to filter by
 * @property {Map} _minAmount - The min amount to filter by
 * @property {Map} _maxAmount - The max amount to filter by
 * @property {Map} _startDate - The start bcms date instance to filter by
 * @property {Map} _endDate - The end bcms date to filter by
 */
 export default class DepositHistorySearch {
    _type;
    _date;
    _description;
    _amount;
    _check;
    _account;
    _minAmount;
    _maxAmount;
    _startDate;
    _endDate;

    _ranges = new Map([ 
        [ "-30", "Last 30 Days" ],
        [ "-60", "Last 60 Days" ],
        [ "-90", "Last 90 Days" ],
        [ "-120", "Last 120 Days" ],
        [ "custom", "Custom Range" ]
    ]);

    _types = new Map([ 
        [ "all", "All Transaction Types" ],
        [ "ach", "ACH" ],
        [ "atm", "ATM" ],
        [ "payment", "Bill Payment" ],
        [ "check", "Check" ],
        [ "deposit", "Deposit" ],
        [ "payroll", "Payroll" ]
    ]);

    constructor(record) {
        this.date = record.date;
        this.description = record.description;
        this.type = record.type;
        this.amount = record.amount;
        this.check = record.check;
        this.account = record.account;
        this.minAmount = record.minAmount;
        this.maxAmount = record.maxAmount;
        this.startDate = record.startDate;
        this.endDate = record.endDate;
    }

    /**
     * @name date
     * @instance
     * @property set {STRING} - sets the date
     * @memberof DepositHistorySearch 
     */
    set date(value) { this._date = value; }
    /**
     * @name description
     * @instance
     * @property set {STRING} - sets the description
     * @memberof DepositHistorySearch
     */
    set description(value) { this._description = value; }
    /**
     * @name type
     * @instance
     * @property set {STRING} - sets the type
     * @memberof DepositHistorySearch
     */
    set type(value) { this._type = value; }
    /**
     * @name amount
     * @instance
     * @property set {STRING} - sets the amount
     * @memberof DepositHistorySearch
     */
    set amount(value) { this._amount = value; }
    /**
     * @name check
     * @instance
     * @property set {STRING} - sets the check number
     * @memberof DepositHistorySearch
     */
    set check(value) { this._check = value; }
     /**
     * @name accounts
     * @instance
     * @property set {STRING} - sets the accounts
     * @memberof DepositHistorySearch
     */
    set account(value) { this._account = value; }
     /**
     * @name minAmount
     * @instance
     * @property set {STRING} - sets the minAmount
     * @memberof DepositHistorySearch
     */
    set minAmount(value) { this._minAmount = value; }
     /**
     * @name maxAmount
     * @instance
     * @property set {STRING} - sets the maxAmount
     * @memberof DepositHistorySearch
     */
    set maxAmount(value) { this._maxAmount = value; }
    /**
     * @name startDate
     * @instance
     * @property set {STRING} - sets the startDate
     * @memberof DepositHistorySearch
     */
     set startDate(value) { this._startDate = value; }
     /**
     * @name endDate
     * @instance
     * @property set {STRING} - sets the endDate
     * @memberof DepositHistorySearch
     */
     set endDate(value) { this._endDate = value; }

    /**
     * @name date
     * @instance
     * @property get {STRING} - returns the date
     * @memberof DepositHistorySearch
     */
    get date() { return this._date; }
    /**
     * @name description
     * @instance
     * @property get {STRING} - returns the description
     * @memberof DepositHistorySearch
     */
    get description() { return this._description; }
    /**
     * @name type
     * @instance
     * @property get {STRING} - returns the type
     * @memberof DepositHistorySearch
     */
    get type() { return this._type; }
    /**
     * @name amount
     * @instance
     * @property get {STRING} - returns the amount
     * @memberof DepositHistorySearch
     */
    get amount() { return this._amount; }
    /**
     * @name check
     * @instance
     * @property get {STRING} - returns the check number
     * @memberof DepositHistorySearch
     */
    get check() { return this._check; }
     /**
     * @name accounts
     * @instance
     * @property get {STRING} - returns all accounts
     * @memberof DepositHistorySearch
     */
     get account() { return this._account; }

    /**
     * @name ranges
     * @instance
     * @property get {Map} - returns the ranges
     * @memberof DepositHistorySearch
     */
    get ranges() { return this._ranges; }
    /**
     * @name types
     * @instance
     * @property get {Map} - returns the types
     * @memberof DepositHistorySearch
     */
    get types() { return this._types; }
    /**
     * @name minAmount
     * @instance
     * @property get {STRING} - returns the minAmount
     * @memberof DepositHistorySearch
     */
    get minAmount() { return this._minAmount; }
    /**
     * @name maxAmount
     * @instance
     * @property get {STRING} - returns the maxAmount
     * @memberof DepositHistorySearch
     */
    get maxAmount() { return this._maxAmount; }
    /**
     * @name startDate
     * @instance
     * @property get {STRING} - returns the startDate
     * @memberof DepositHistorySearch
     */
    get startDate() { return this._startDate; }
    /**
     * @name endDate
     * @instance
     * @property get {STRING} - returns the endDate
     * @memberof DepositHistorySearch
     */
    get endDate() { return this._endDate; }

    /**
     * @name filters
     * @instance
     * @property get {OBJECT} - returns the set filter properties
     * @memberof DepositHistorySearch
     */
    get filters() {
        return Object.getOwnPropertyNames(this).reduce((result, name) => {
            const key = name.slice(1);
            const value = this[key];

            if(value !== undefined && value !== "" && (key !== "ranges" && key !== "types")){
                result[key] = value;
            }

            return result;
        }, {});
    }
 
    /**
     * @name record
     * @instance
     * @property get {OBJECT} - returns the set values
     * @memberof DepositHistorySearch
     */
    get record() {
      return {
        date: this.date,
        description: this.description,
        type: this.type,
        amount: this.amount,
        check: this.check,
        account: this.account,
        minAmount: this.minAmount,
        maxAmount: this.maxAmount,
        startDate: this.startDate,
        endDate: this.endDate
      };
    }

    checkValue(deposit, key) {
        switch (key) {
            case "date":
              if (this.date === "custom" && this.startDate && this.endDate) {
                // If this deposit date is within the start/end date range, return the deposit.
                if ((deposit.date.isAfter(this.startDate) || deposit.date.isEqual(this.startDate))
                  && deposit.date.isBefore(this.endDate) || deposit.date.isEqual(this.endDate)) {
                  return true;
                  // Else, deposit is not in range so do not return this deposit. 
                } else {
                  return false;
                }
              } else {
                if (this.date === "custom") { return false }
                return deposit.date.isAfter(BCMSDate.init().offset(this.date));
              }
            case "account":
              return deposit.account.id === this.account.id || this.account === "all";
            case "amount":      
              const amount = parseFloat(deposit.amount.amount);
              return parseFloat(this.amount.amount) === (amount < 0 ? -amount : amount);
            case "minAmount":
              const amount1 = parseFloat(deposit.amount.amount);
              return parseFloat(this.minAmount.amount) <= amount1;
            case "maxAmount":
              const amount2 = parseFloat(deposit.amount.amount);
              return parseFloat(this.maxAmount.amount) >= amount2;
            default:
              return true;
          }
    }

    filter() {
        return Deposit.getSortedDeposits()
            .filter(deposit => {
                return Object.keys(this.filters).reduce((result, key) => {
                    if(!this.checkValue(deposit, key)) {
                        result = false;
                    }

                    return result;
                }, true);
            }, []);
    }

    reset() {
        return DepositHistorySearch.init();
    }
    
    static init(record) {
        return new DepositHistorySearch({ type: "all", date: "-120", account: "all", ...record ?? {} });
    }
}